package com.rosario.juridico.ui.screens

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.AutoAwesome
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material3.*
import androidx.compose.material3.MaterialTheme.colorScheme
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.rosario.juridico.platform.BackendClient
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.premium.PremiumManager
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.domain.AccessState
import com.rosario.juridico.platform.UserQuota
import com.rosario.juridico.platform.checkAccess
import com.rosario.juridico.platform.AccessResult
import kotlinx.coroutines.launch
import androidx.navigation.NavController
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import com.rosario.juridico.platform.ChatMessage

@Composable
fun PantallaConsulta(
    navController: NavController,
    onAgendarEvento: ((titulo: String, fecha: String, descripcion: String) -> Unit)? = null,
    initialQuery: String? = null
) {
    val initialText = remember {
        val fromRoute = initialQuery?.takeIf { it.isNotBlank() }
        val fromManager = AppUsageManager.pendingAiQuery?.takeIf { it.isNotBlank() }
        AppUsageManager.pendingAiQuery = null
        fromRoute ?: fromManager ?: ""
    }
    var pregunta     by remember { mutableStateOf(initialText) }
    val chatMessages = remember { mutableStateListOf<ChatMessage>() }
    var estaCargando by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    
    val scope = rememberCoroutineScope()
    val listState = rememberLazyListState()
    val keyboardController = LocalSoftwareKeyboardController.current

    // Auto-scroll al agregar mensajes
    LaunchedEffect(chatMessages.size, estaCargando) {
        if (chatMessages.isNotEmpty()) {
            listState.animateScrollToItem(chatMessages.size - 1)
        }
    }

    // ── VERIFICACIÓN DE SESIÓN REACTIVA ──
    val userEmail by AppUsageManager.instance.currentUserEmail.collectAsState()
    val isLoggedIn = userEmail != null

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(colorScheme.background)
            .imePadding()
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // Bloqueo por falta de Inicio de Sesión
        if (!isLoggedIn) {
            Card(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                shape = RoundedCornerShape(20.dp),
                colors = CardDefaults.cardColors(containerColor = colorScheme.surfaceVariant.copy(alpha = 0.5f))
            ) {
                Column(
                    modifier = Modifier.padding(24.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Icon(
                        Icons.Default.Lock, 
                        null, 
                        tint = colorScheme.primary, 
                        modifier = Modifier.size(48.dp)
                    )
                    Text(
                        "Acceso Restringido",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = colorScheme.primary
                    )
                    Text(
                        "Debes iniciar sesión para realizar consultas a nuestra IA.",
                        textAlign = TextAlign.Center,
                        style = MaterialTheme.typography.bodyMedium
                    )
                    Button(
                        onClick = { 
                            navController.navigate(Routes.login(register = false))
                        },
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text("Iniciar Sesión")
                    }
                }
            }
        } else {
            // ── Header ────────────────────────────────────────────────────────
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape    = RoundedCornerShape(16.dp),
                colors   = CardDefaults.cardColors(containerColor = colorScheme.primaryContainer)
            ) {
                Row(
                    modifier          = Modifier.padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Default.AutoAwesome, null, tint = colorScheme.primary, modifier = Modifier.size(32.dp))
                    Spacer(Modifier.width(12.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Asistente Legal con IA", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                        Text("Consultá sobre plazos y cuestiones jurídicas", style = MaterialTheme.typography.bodySmall)
                    }
                    if (chatMessages.isNotEmpty()) {
                        IconButton(
                            onClick = {
                                chatMessages.clear()
                                keyboardController?.hide()
                            }
                        ) {
                            Icon(
                                imageVector = Icons.Default.Delete,
                                contentDescription = "Limpiar historial",
                                tint = colorScheme.primary.copy(alpha = 0.7f)
                            )
                        }
                    }
                }
            }

            // ── Área de Chat ──────────────────────────────────────────────────
            LazyColumn(
                state = listState,
                modifier = Modifier.weight(1f).fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp),
                contentPadding = PaddingValues(vertical = 8.dp)
            ) {
                items(chatMessages) { msg ->
                    ChatBubble(msg)
                }
                
                if (estaCargando) {
                    item {
                        Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.CenterStart) {
                            Card(
                                shape = RoundedCornerShape(12.dp),
                                colors = CardDefaults.cardColors(containerColor = colorScheme.surfaceVariant)
                            ) {
                                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                    CircularProgressIndicator(modifier = Modifier.size(16.dp), strokeWidth = 2.dp)
                                    Spacer(Modifier.width(8.dp))
                                    Text("Escribiendo...", style = MaterialTheme.typography.bodySmall)
                                }
                            }
                        }
                    }
                }
            }

            // ── Input y Botón ─────────────────────────────────────────────────
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                if (errorMessage != null) {
                    Text(errorMessage!!, color = colorScheme.error, fontSize = 12.sp, modifier = Modifier.padding(horizontal = 4.dp))
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.Bottom,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedTextField(
                        value         = pregunta,
                        onValueChange = { pregunta = it; errorMessage = null },
                        placeholder   = { Text("Escribe tu consulta...") },
                        modifier      = Modifier.weight(1f),
                        maxLines      = 4,
                        shape         = RoundedCornerShape(24.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            unfocusedContainerColor = colorScheme.surface,
                            focusedContainerColor = colorScheme.surface
                        )
                    )

                    val userStatus by AppUsageManager.instance.userStatus.collectAsState()
                    val accessState = AppUsageManager.instance.checkAccess()
                    val isPremium = accessState == AccessState.PREMIUM_ACTIVE
                    
                    val quota = UserQuota(
                        initial_uses_remaining = userStatus.initial_uses_remaining,
                        last_monthly_use_timestamp = userStatus.last_monthly_use_timestamp
                    )
                    val accessResult = checkAccess(quota)
                    val isLocked = !isPremium && accessResult == AccessResult.DENIED

                    CalculatorActionLock(
                        navController = navController,
                        onAction = {
                            if (pregunta.isNotBlank() && !estaCargando) {
                                scope.launch {
                                    val userMsg = ChatMessage(role = "user", content = pregunta)
                                    chatMessages.add(userMsg)
                                    val currentPrompt = pregunta
                                    pregunta = ""
                                    estaCargando = true
                                    errorMessage = null
                                    
                                    try {
                                        val res = BackendClient.consultaLegal(
                                            prompt = currentPrompt,
                                            history = chatMessages.toList()
                                        )
                                        if (res.error != null) {
                                            errorMessage = res.error
                                        } else {
                                            chatMessages.add(ChatMessage(role = "assistant", content = res.result))
                                        }
                                    } catch (e: Exception) {
                                        errorMessage = "Error de conexión."
                                    } finally {
                                        estaCargando = false
                                    }
                                }
                            }
                        }
                    ) { onClick ->
                        FilledIconButton(
                            onClick = onClick,
                            enabled = !estaCargando && pregunta.isNotBlank(),
                            modifier = Modifier.size(52.dp),
                            shape = RoundedCornerShape(16.dp)
                        ) {
                            if (isLocked) {
                                Icon(Icons.Default.Lock, null)
                            } else {
                                Icon(Icons.AutoMirrored.Filled.Send, null)
                            }
                        }
                    }
                }
                
                // Aviso IA pequeño
                Text(
                    text = "⚠️ La IA puede cometer errores. Verificá la normativa.",
                    modifier = Modifier.fillMaxWidth().padding(bottom = 4.dp),
                    style = MaterialTheme.typography.labelSmall,
                    textAlign = TextAlign.Center,
                    color = colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
                )
            }
        }
    }
}

@Composable
fun ChatBubble(message: ChatMessage) {
    val isUser = message.role == "user"
    val alignment = if (isUser) Alignment.CenterEnd else Alignment.CenterStart
    val bgColor = if (isUser) colorScheme.primaryContainer else colorScheme.surfaceVariant
    val textColor = if (isUser) colorScheme.onPrimaryContainer else colorScheme.onSurfaceVariant
    val shape = if (isUser) {
        RoundedCornerShape(16.dp, 16.dp, 4.dp, 16.dp)
    } else {
        RoundedCornerShape(16.dp, 16.dp, 16.dp, 4.dp)
    }

    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = alignment) {
        if (!isUser) {
            // Burbuja IA con Caja Accionable
            val textoCopia = """
                CONSULTA IA - JURÍDICO ROSARIO
                -------------------------------------------
                ${message.content}
                -------------------------------------------
                Consultado en www.juridicorosario.com
            """.trimIndent()

            CajaResultadoAccionable(
                titulo = "Asistente Jurídico",
                textoParaCompartir = textoCopia,
                colorFondo = bgColor
            ) {
                Text(
                    text = message.content,
                    style = MaterialTheme.typography.bodyMedium,
                    color = textColor
                )
            }
        } else {
            // Burbuja Usuario simple
            Surface(
                color = bgColor,
                shape = shape,
                modifier = Modifier.fillMaxWidth(0.85f)
            ) {
                Text(
                    text = message.content,
                    modifier = Modifier.padding(12.dp),
                    style = MaterialTheme.typography.bodyMedium,
                    color = textColor
                )
            }
        }
    }
}
